import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../utils/apiConfig';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useMasterData } from '../contexts/MasterDataContext';
import { Trophy, Plus, Edit2, Trash2, Users, Calendar, MapPin, Loader2, Settings2, Share2, Copy, Check, Sliders } from 'lucide-react';
import { EventFormDialog } from '../components/events/EventFormDialog';
import { RegistrationsPanel } from '../components/events/RegistrationsPanel';
import { STATUS_STYLES, statusLabel, fileUrl } from '../components/events/eventUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

export default function EventsPage() {
  const { isStaff, isEventAdmin } = useAuth();
  const { getVenueNames } = useMasterData();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);   // event whose registrations are open
  const [deleting, setDeleting] = useState(null);
  const [playerBase, setPlayerBase] = useState('https://play.makspickleball.com');
  const [sharing, setSharing] = useState(null);   // event being shared
  const [copied, setCopied] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showMasters, setShowMasters] = useState(false);
  const [masters, setMasters] = useState({ tshirt_sizes: [], self_ratings: [] });
  const [mastersInput, setMastersInput] = useState({ tshirt_new: '', rating_new: '' });
  const [savingMasters, setSavingMasters] = useState(false);
  const [template, setTemplate] = useState('');
  const [notifyMobiles, setNotifyMobiles] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [mobileInput, setMobileInput] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);

  const openSettings = async () => {
    try {
      const res = await axios.get(`${API}/events/settings/template`);
      setTemplate(res.data.template_name || 'tournamentconfirm');
      setNotifyMobiles(res.data.notify_mobiles || []);
      setNotifyEmail(res.data.notify_email || '');
      setMobileInput('');
      setShowSettings(true);
    } catch (e) { toast.error('Failed to load settings'); }
  };
  const addMobile = () => {
    const m = mobileInput.trim();
    if (!/^\d{10}$/.test(m)) { toast.error('Enter a valid 10-digit mobile'); return; }
    if (notifyMobiles.includes(m)) { toast.error('Already added'); return; }
    setNotifyMobiles([...notifyMobiles, m]);
    setMobileInput('');
  };
  const saveTemplate = async () => {
    setSavingTpl(true);
    try {
      await axios.put(`${API}/events/settings/template`, {
        template_name: template, notify_mobiles: notifyMobiles, notify_email: notifyEmail,
      });
      toast.success('Notification settings saved');
      setShowSettings(false);
    } catch (e) { toast.error('Failed to save'); } finally { setSavingTpl(false); }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data || []);
      // keep the managing panel in sync (updated slot counts)
      if (managing) {
        const fresh = (res.data || []).find((e) => e.id === managing.id);
        if (fresh) setManaging(fresh);
      }
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    axios.get(`${API}/events/settings/template`)
      .then((r) => r.data.player_base_url && setPlayerBase(r.data.player_base_url))
      .catch(() => {});
  }, []);

  const loadMasters = () =>
    axios.get(`${API}/events/masters`)
      .then((r) => setMasters(r.data))
      .catch(() => {});

  const slugify = (s) =>
    (s || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

  const eventLink = (ev) => {
    const params = new URLSearchParams({
      utm_source: 'whatsapp',
      utm_medium: 'share',
      utm_campaign: slugify(ev.name),
      utm_content: `event_${(ev.id || '').slice(0, 8)}`,
    });
    return `${playerBase}/api/events/og/${ev.id}?${params.toString()}`;
  };

  const shareMessage = (ev) => {
    const lines = [];
    lines.push(`🏆 *${ev.name}*`);
    if (ev.venue) lines.push(`📍 ${ev.venue}`);
    if (ev.start_date) lines.push(`📅 ${ev.start_date}${ev.end_date && ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ''}`);
    if ((ev.categories || []).length) {
      lines.push('');
      lines.push('*Categories:*');
      ev.categories.forEach((c) => lines.push(`• ${c.name} – ₹${c.fee}${c.slots_left !== null ? ` (${c.slots_left} slots left)` : ''}`));
    }
    lines.push('');
    lines.push(`Register now 👉 ${eventLink(ev)}`);
    const coupon = ev.coupon;
    if (ev.allow_coupons && coupon?.code && coupon.is_active !== false) {
      const today = new Date().toISOString().slice(0, 10);
      const inWindow = (!coupon.valid_from || today >= coupon.valid_from) && (!coupon.valid_to || today <= coupon.valid_to);
      if (inWindow) {
        const discountText = coupon.discount_type === 'flat' ? `₹${coupon.discount_value} off` : `${coupon.discount_value}% off`;
        lines.push('');
        lines.push(`🎉 Use code *${coupon.code}* for ${discountText}!`);
      }
    }
    lines.push('');
    lines.push('_See you on the court! — MAKS Pickleball_');
    return lines.join('\n');
  };

  const copyText = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(''), 1500);
    } catch (e) {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const handleDelete = async (ev) => {
    try {
      const res = await axios.delete(`${API}/events/${ev.id}`);
      toast.success(res.data.message || 'Deleted');
      setDeleting(null);
      fetchEvents();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (managing) {
    return (
      <DashboardLayout>
        {isEventAdmin && events.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="event-switcher">
            <span className="text-xs text-slate-500">Switch event:</span>
            {events.map((ev) => (
              <button key={ev.id} onClick={() => setManaging(ev)} data-testid={`switch-event-${ev.id}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${ev.id === managing.id ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{ev.name}</button>
            ))}
          </div>
        )}
        <RegistrationsPanel key={managing.id} event={managing} onBack={() => { setManaging(null); fetchEvents(); }} onRefreshEvent={fetchEvents} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Trophy className="w-6 h-6 text-primary" /> Events</h1>
          <p className="text-slate-500 text-sm mt-1">{isEventAdmin ? 'Your assigned events — manage details & registrations' : 'Create tournaments, manage categories & player registrations'}</p>
        </div>
        {!isEventAdmin && <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadMasters(); setShowMasters(true); }} disabled={isStaff}
                  title={isStaff ? 'Read-only for Staff' : ''}
                  data-testid="event-masters-btn">
            <Sliders className="w-4 h-4 mr-1" /> Masters
          </Button>
          <Button variant="outline" onClick={openSettings} disabled={isStaff}
                  title={isStaff ? 'Read-only for Staff' : ''}
                  data-testid="event-settings-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Notifications
          </Button>
          <Button className="bg-primary hover:bg-primary-hover"
                  onClick={() => { setEditing(null); setShowForm(true); }}
                  disabled={isStaff}
                  title={isStaff ? 'Read-only for Staff' : ''}
                  data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-1" /> Create Event
          </Button>
        </div>}
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : events.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" data-testid="events-empty">
          <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No events yet. Create your first tournament.
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {events.map((ev) => (
            <Card key={ev.id} className="overflow-hidden" data-testid={`event-card-${ev.id}`}>
              {ev.banner_file_id ? (
                <img src={fileUrl(ev.banner_file_id)} alt={ev.name} className="h-40 w-full object-cover" />
              ) : (
                <div className="h-40 w-full bg-gradient-to-br from-primary/20 to-slate-200 flex items-center justify-center">
                  <Trophy className="w-10 h-10 text-primary/50" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-lg text-slate-900">{ev.name}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${STATUS_STYLES[ev.status] || 'bg-slate-100'}`} data-testid={`event-status-${ev.id}`}>
                    {statusLabel(ev.status)}
                  </span>
                </div>
                {ev.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{ev.description}</p>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
                  {ev.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ev.venue}</span>}
                  {ev.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ''}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {ev.total_registrations || 0} registered</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(ev.categories || []).map((c) => (
                    <span key={c.id} className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                      {c.name} · ₹{c.fee}{c.slots_left !== null ? ` · ${c.slots_left} left` : ''}
                    </span>
                  ))}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button size="sm" className="bg-primary hover:bg-primary-hover flex-1" onClick={() => setManaging(ev)} data-testid={`manage-event-${ev.id}`}>
                    <Users className="w-4 h-4 mr-1" /> Manage Registrations
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSharing(ev)} data-testid={`share-event-${ev.id}`}><Share2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(ev); setShowForm(true); }} data-testid={`edit-event-${ev.id}`}><Edit2 className="w-4 h-4" /></Button>
                  {!isEventAdmin && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleting(ev)} data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <EventFormDialog
          open={showForm}
          event={editing}
          venues={getVenueNames()}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchEvents(); }}
        />
      )}

      {sharing && (
        <Dialog open={!!sharing} onOpenChange={() => setSharing(null)}>
          <DialogContent className="sm:max-w-[480px]" data-testid="share-event-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-primary" /> Share “{sharing.name}”</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {sharing.banner_file_id && (
                <img src={fileUrl(sharing.banner_file_id)} alt="banner" className="h-32 w-full object-cover rounded-lg" />
              )}
              <div className="space-y-1.5">
                <Label>Player Registration Link</Label>
                <div className="flex gap-2">
                  <Input readOnly value={eventLink(sharing)} className="text-xs" data-testid="share-link-input" />
                  <Button variant="outline" size="sm" onClick={() => copyText(eventLink(sharing), 'link')} data-testid="copy-link-btn">
                    {copied === 'link' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp Broadcast Message</Label>
                <Textarea readOnly rows={9} value={shareMessage(sharing)} className="text-xs font-mono" data-testid="share-message-text" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => copyText(shareMessage(sharing), 'msg')} data-testid="copy-message-btn">
                  {copied === 'msg' ? <><Check className="w-4 h-4 mr-1 text-emerald-600" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Message</>}
                </Button>
                <a href={`https://wa.me/?text=${encodeURIComponent(shareMessage(sharing))}`} target="_blank" rel="noreferrer" className="flex-1" data-testid="open-whatsapp-btn">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1da851] text-white"><Share2 className="w-4 h-4 mr-1" /> Open WhatsApp</Button>
                </a>
              </div>
              <p className="text-xs text-slate-400">This link shows a rich banner preview when shared on WhatsApp/Instagram and opens the event page (players sign in only at the Register step).</p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showSettings && (
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="sm:max-w-[440px]" data-testid="event-settings-dialog">
            <DialogHeader><DialogTitle>Tournament Notifications</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Approved WABA Template Name</Label>
                <Input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="tournamentconfirm" data-testid="template-name-input" />
                <p className="text-xs text-slate-500">Sent to players on confirmed registration. Params: player name, event name, venue, event date, amount.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Additional Notification Mobiles (WhatsApp)</Label>
                <div className="flex gap-2">
                  <Input value={mobileInput} onChange={(e) => setMobileInput(e.target.value)} placeholder="10-digit mobile"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMobile())} data-testid="notify-mobile-input" />
                  <Button type="button" variant="outline" onClick={addMobile} data-testid="add-notify-mobile">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {notifyMobiles.map((m) => (
                    <span key={m} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700" data-testid={`notify-mobile-${m}`}>
                      {m}
                      <button onClick={() => setNotifyMobiles(notifyMobiles.filter((x) => x !== m))} className="text-red-500 font-bold ml-1">×</button>
                    </span>
                  ))}
                  {notifyMobiles.length === 0 && <span className="text-xs text-slate-400">No extra recipients</span>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Notification Email</Label>
                <Input value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="makspickleball@gmail.com" data-testid="notify-email-input" />
                <p className="text-xs text-slate-500">Emailed on every confirmed registration.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={saveTemplate} disabled={savingTpl} className="bg-primary hover:bg-primary-hover" data-testid="save-template-btn">{savingTpl ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showMasters && (
        <Dialog open={showMasters} onOpenChange={setShowMasters}>
          <DialogContent className="sm:max-w-[460px]" data-testid="event-masters-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sliders className="w-5 h-5 text-primary" /> Event Masters</DialogTitle></DialogHeader>
            <div className="space-y-5">
              {/* T-Shirt Sizes */}
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide mb-2 block">T-Shirt Sizes</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(masters.tshirt_sizes || []).map((s) => (
                    <span key={s} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700" data-testid={`tshirt-size-chip-${s}`}>
                      {s}
                      <button onClick={() => setMasters((m) => ({ ...m, tshirt_sizes: m.tshirt_sizes.filter((x) => x !== s) }))} className="text-red-500 font-bold ml-0.5 hover:text-red-700">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={mastersInput.tshirt_new} onChange={(e) => setMastersInput({ ...mastersInput, tshirt_new: e.target.value })}
                    placeholder="Add size e.g. 3XL" data-testid="tshirt-size-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = mastersInput.tshirt_new.trim(); if (v && !masters.tshirt_sizes.includes(v)) setMasters((m) => ({ ...m, tshirt_sizes: [...m.tshirt_sizes, v] })); setMastersInput({ ...mastersInput, tshirt_new: '' }); } }} />
                  <Button type="button" variant="outline" onClick={() => { const v = mastersInput.tshirt_new.trim(); if (v && !masters.tshirt_sizes.includes(v)) setMasters((m) => ({ ...m, tshirt_sizes: [...m.tshirt_sizes, v] })); setMastersInput({ ...mastersInput, tshirt_new: '' }); }} data-testid="add-tshirt-size-btn">Add</Button>
                </div>
              </div>
              {/* Self Ratings */}
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide mb-2 block">Self Rating Options</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(masters.self_ratings || []).map((r) => (
                    <span key={r} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-700" data-testid={`rating-chip-${r}`}>
                      {r}
                      <button onClick={() => setMasters((m) => ({ ...m, self_ratings: m.self_ratings.filter((x) => x !== r) }))} className="text-red-500 font-bold ml-0.5 hover:text-red-700">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={mastersInput.rating_new} onChange={(e) => setMastersInput({ ...mastersInput, rating_new: e.target.value })}
                    placeholder="e.g. Advanced" data-testid="self-rating-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = mastersInput.rating_new.trim(); if (v && !masters.self_ratings.includes(v)) setMasters((m) => ({ ...m, self_ratings: [...m.self_ratings, v] })); setMastersInput({ ...mastersInput, rating_new: '' }); } }} />
                  <Button type="button" variant="outline" onClick={() => { const v = mastersInput.rating_new.trim(); if (v && !masters.self_ratings.includes(v)) setMasters((m) => ({ ...m, self_ratings: [...m.self_ratings, v] })); setMastersInput({ ...mastersInput, rating_new: '' }); }} data-testid="add-self-rating-btn">Add</Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMasters(false)}>Cancel</Button>
              <Button onClick={async () => { setSavingMasters(true); try { await axios.put(`${API}/events/masters`, { tshirt_sizes: masters.tshirt_sizes, self_ratings: masters.self_ratings }); toast.success('Masters saved'); setShowMasters(false); } catch { toast.error('Failed to save'); } finally { setSavingMasters(false); } }} disabled={savingMasters} className="bg-primary hover:bg-primary-hover" data-testid="save-masters-btn">{savingMasters ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDeleting(null)}>
          <Card className="p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-event-dialog">
            <h3 className="font-bold text-lg mb-2">Delete Event?</h3>
            <p className="text-sm text-slate-600 mb-4">Delete <strong>{deleting.name}</strong>? Events with active registrations are cancelled instead of deleted.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleting)} data-testid="confirm-delete-event">Delete</Button>
            </div>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
